/** Catálogo e inventario de materias primas (módulo Producción). */

import {
  inventoryQtyFromPurchase,
  packContentFromUnit,
  formatInventoryQty,
} from '../../utils/packFormatQty';

export { packContentFromUnit, inventoryQtyFromPurchase, formatInventoryQty };

const MATERIALS_KEY = 'sqmt.production.raw-materials.v6';
const LOTS_KEY = 'sqmt.production.mp-lots.v4';
const RECEPTIONS_KEY = 'sqmt.production.mp-receptions.v4';
const INGRESOS_KEY = 'sqmt.production.mp-ingresos.v4';
const EGRESOS_KEY = 'sqmt.production.mp-egresos.v4';
export const RAW_MATERIALS_CHANGED_EVENT = 'sqmt-production-changed';

/** Código de 6 caracteres: 2 letras de categoría + 4 correlativos (RS0001). */
export const DEFAULT_MATERIAL_CATEGORY_PREFIX = {
  Resinas: 'RS',
  Diluyentes: 'DY',
  Extendedores: 'EX',
  Pigmentos: 'PG',
  Aditivos: 'AD',
  Envases: 'EN',
  Etiquetas: 'EQ',
};

/** @deprecated Use getMaterialCategoryPrefixMap() — kept for callers that need static defaults. */
export const MATERIAL_CATEGORY_PREFIX = DEFAULT_MATERIAL_CATEGORY_PREFIX;

export const MATERIAL_FAMILIES = Object.keys(DEFAULT_MATERIAL_CATEGORY_PREFIX);

const CUSTOM_CATEGORIES_KEY = 'sqmt.production.mp-categories.v1';
const CUSTOM_TECH_PARAMS_KEY = 'sqmt.production.mp-tech-params.v1';
export const RAW_MATERIALS_CATALOGS_CHANGED_EVENT = 'sqmt-production-mp-catalogs-changed';

const notifyRawMaterialCatalogsChanged = (detail = {}) => {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(RAW_MATERIALS_CATALOGS_CHANGED_EVENT, { detail }));
  } catch {
    // ignore
  }
};

export const loadCustomMaterialCategories = () => {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(CUSTOM_CATEGORIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => ({
        name: String(row?.name || '').trim(),
        prefix: String(row?.prefix || '')
          .trim()
          .toUpperCase()
          .slice(0, 2),
      }))
      .filter((row) => row.name && /^[A-Z]{2}$/.test(row.prefix));
  } catch {
    return [];
  }
};

export const saveCustomMaterialCategories = (rows) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify(rows || []));
    notifyRawMaterialCatalogsChanged({ key: 'categories' });
  } catch {
    // ignore
  }
};

export const getMaterialCategoryPrefixMap = () => {
  const map = { ...DEFAULT_MATERIAL_CATEGORY_PREFIX };
  loadCustomMaterialCategories().forEach((row) => {
    if (!map[row.name]) map[row.name] = row.prefix;
  });
  return map;
};

export const getMaterialFamilies = () => Object.keys(getMaterialCategoryPrefixMap());

export const upsertMaterialCategory = ({ name, prefix }) => {
  const cleanName = String(name || '').trim();
  const cleanPrefix = String(prefix || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  if (cleanName.length < 2) {
    return { ok: false, error: 'Indica el nombre de la categoría (mín. 2 caracteres).' };
  }
  if (!/^[A-Z]{2}$/.test(cleanPrefix)) {
    return { ok: false, error: 'El prefijo debe ser exactamente 2 letras (ej. RS).' };
  }
  const currentMap = getMaterialCategoryPrefixMap();
  const existingByName = currentMap[cleanName];
  if (existingByName && existingByName !== cleanPrefix) {
    return { ok: false, error: `La categoría “${cleanName}” ya existe con prefijo ${existingByName}.` };
  }
  const takenByOther = Object.entries(currentMap).find(
    ([family, code]) => code === cleanPrefix && family !== cleanName
  );
  if (takenByOther) {
    return { ok: false, error: `El prefijo ${cleanPrefix} ya está usado por “${takenByOther[0]}”.` };
  }
  if (DEFAULT_MATERIAL_CATEGORY_PREFIX[cleanName]) {
    return { ok: true, name: cleanName, prefix: cleanPrefix, existing: true };
  }
  const custom = loadCustomMaterialCategories().filter((row) => row.name !== cleanName);
  custom.push({ name: cleanName, prefix: cleanPrefix });
  saveCustomMaterialCategories(custom);
  return { ok: true, name: cleanName, prefix: cleanPrefix, existing: false };
};

export const loadCustomTechParamSuggestions = () => {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.localStorage.getItem(CUSTOM_TECH_PARAMS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => ({
        name: String(row?.name || '').trim(),
        unit: String(row?.unit || '').trim(),
      }))
      .filter((row) => row.name);
  } catch {
    return [];
  }
};

export const saveCustomTechParamSuggestions = (rows) => {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CUSTOM_TECH_PARAMS_KEY, JSON.stringify(rows || []));
    notifyRawMaterialCatalogsChanged({ key: 'tech_params' });
  } catch {
    // ignore
  }
};

/** Ítems de catálogo de adquisiciones que pueden vincularse a una MP de producción. */
export const LINKABLE_PROCUREMENT_TYPES = ['Materias primas', 'Insumos'];

export const foldProcurementType = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const isLinkableProcurementType = (type) =>
  LINKABLE_PROCUREMENT_TYPES.some((item) => foldProcurementType(item) === foldProcurementType(type));

export const isLinkableProcurementItem = (item = {}) => {
  const code = String(item.item_code || item.sku || item.code || item.procurement_code || '')
    .trim()
    .toUpperCase();
  if (code.startsWith('MP-') || code.startsWith('INS-')) return true;
  return isLinkableProcurementType(item.item_type || item.kind || item.procurement_type || '');
};

export const procurementTypeForFamily = (family) =>
  family === 'Envases' || family === 'Etiquetas' ? 'Insumos' : 'Materias primas';

export const PACKAGING_FAMILIES = ['Envases', 'Etiquetas'];

export const isPackagingFamily = (family) => PACKAGING_FAMILIES.includes(family);

export const inferredProcurementType = (code, itemType = '') => {
  const typed = String(itemType || '').trim();
  if (typed) return typed;
  const value = String(code || '').trim().toUpperCase();
  if (value.startsWith('INS-')) return 'Insumos';
  if (value.startsWith('MP-')) return 'Materias primas';
  return '';
};

export const familyForProcurementType = (type, currentFamily = 'Resinas') => {
  const folded = foldProcurementType(type);
  if (folded === 'insumos') return isPackagingFamily(currentFamily) ? currentFamily : 'Envases';
  if (folded === 'materias primas') return isPackagingFamily(currentFamily) ? 'Resinas' : currentFamily;
  return currentFamily;
};

export const procurementLinkLabel = (row) => {
  const code = String(row?.procurement_code || '').trim();
  if (!code) return '—';
  const name = String(row?.procurement_name || '').trim();
  return name ? `${code} · ${name}` : code;
};

export const familyFromCode = (code) => {
  const prefix = String(code || '').trim().slice(0, 2).toUpperCase();
  return (
    Object.entries(getMaterialCategoryPrefixMap()).find(([, value]) => value === prefix)?.[0] || ''
  );
};

export const familyOptionLabel = (family) => {
  const prefix = getMaterialCategoryPrefixMap()[family];
  return prefix ? `${prefix} · ${family}` : family;
};

export const MATERIAL_UNITS = ['kg', 'L', 'UN'];
export const MATERIAL_STATUSES = ['Activa', 'Obsoleta'];
export const HAZARD_CLASSES = ['No peligroso', 'Irritante', 'Inflamable', 'Tóxico', 'Corrosivo'];
export const MP_WAREHOUSES = ['Bodega MP', 'Bodega JR 1', 'Bodega Planta 2'];

export const MATERIAL_STATUS_COLORS = {
  Activa: '#16a34a',
  Obsoleta: '#9ca3af',
};

export const STOCK_STATUS_COLORS = {
  Activo: '#16a34a',
  'Bajo stock': '#d97706',
  'Sin stock': '#dc2626',
};

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

export const seedMaterials = () => [
  {
    id: 1,
    code: 'RS0001',
    procurement_code: 'MP-001',
    procurement_name: 'Resina epóxica bisfenol-A',
    procurement_type: 'Materias primas',
    name: 'Resina epóxica bisfenol-A',
    family: 'Resinas',
    chemical_name: 'Polímero epóxico bisfenol-A / epiclorhidrina',
    cas_number: '25068-38-6',
    chemical_formula: '(C21H24O4)n',
    density: 1.16,
    solids_pct: 100,
    voc: 0,
    flash_point_c: 251,
    viscosity: '12.000 cP @ 25 °C',
    color_index: '',
    appearance: 'Líquido viscoso ámbar',
    hazard_class: 'Irritante',
    packing: 'Tambor 200 kg',
    storage: 'Techado, 5–30 °C, evitar humedad',
    unit: 'kg',
    min_stock: 400,
    typical_supplier: 'Dow / distribuidor local',
    status: 'Activa',
    has_sds: true,
    notes: 'Base de A006 y BW01. Homogeneizar antes de dosificar.',
    updated_at: isoDaysAgo(4),
  },
  {
    id: 2,
    code: 'PG0001',
    procurement_code: 'MP-002',
    procurement_name: 'Dióxido de titanio rutilo',
    procurement_type: 'Materias primas',
    name: 'Dióxido de titanio rutilo',
    family: 'Pigmentos',
    chemical_name: 'Dióxido de titanio',
    cas_number: '13463-67-7',
    chemical_formula: 'TiO₂',
    density: 4.23,
    solids_pct: 100,
    voc: 0,
    flash_point_c: null,
    viscosity: 'Sólido / polvo',
    color_index: 'PW6',
    appearance: 'Polvo blanco fino',
    hazard_class: 'No peligroso',
    packing: 'Saco 25 kg',
    storage: 'Seco, cerrado, paletizado',
    unit: 'kg',
    min_stock: 250,
    typical_supplier: 'Kronos',
    status: 'Activa',
    has_sds: true,
    notes: 'Opacidad y blancura. No compactar el saco abierto.',
    updated_at: isoDaysAgo(12),
  },
  {
    id: 3,
    code: 'DY0001',
    procurement_code: 'MP-003',
    procurement_name: 'Xileno (mezcla de isómeros)',
    procurement_type: 'Materias primas',
    name: 'Xileno (mezcla de isómeros)',
    family: 'Diluyentes',
    chemical_name: 'Dimetilbenceno',
    cas_number: '1330-20-7',
    chemical_formula: 'C8H10',
    density: 0.87,
    solids_pct: 0,
    voc: 870,
    flash_point_c: 25,
    viscosity: '0,7 cP @ 20 °C',
    color_index: '',
    appearance: 'Líquido incoloro',
    hazard_class: 'Inflamable',
    packing: 'Tambor 180 L',
    storage: 'Bodega inflamables, ventilada',
    unit: 'L',
    min_stock: 200,
    typical_supplier: 'Oxiquim',
    status: 'Activa',
    has_sds: true,
    notes: 'VOC alto. Usar con EPP y extracción.',
    updated_at: isoDaysAgo(6),
  },
  {
    id: 4,
    code: 'EX0001',
    procurement_code: 'MP-004',
    procurement_name: 'Carbonato de calcio micronizado',
    procurement_type: 'Materias primas',
    name: 'Carbonato de calcio micronizado',
    family: 'Extendedores',
    chemical_name: 'Carbonato de calcio',
    cas_number: '471-34-1',
    chemical_formula: 'CaCO₃',
    density: 2.71,
    solids_pct: 100,
    voc: 0,
    flash_point_c: null,
    viscosity: 'Sólido / polvo',
    color_index: '',
    appearance: 'Polvo blanco',
    hazard_class: 'No peligroso',
    packing: 'Saco 25 kg',
    storage: 'Seco, cubierto',
    unit: 'kg',
    min_stock: 300,
    typical_supplier: 'Melón Minerales',
    status: 'Activa',
    has_sds: true,
    notes: 'Extender de sólidos. Controlar humedad para no aglomerar.',
    updated_at: isoDaysAgo(20),
  },
  {
    id: 5,
    code: 'AD0001',
    procurement_code: 'MP-005',
    procurement_name: 'Antiespumante siliconado',
    procurement_type: 'Materias primas',
    name: 'Antiespumante siliconado',
    family: 'Aditivos',
    chemical_name: 'Polidimetilsiloxano modificado',
    cas_number: '9016-00-6',
    chemical_formula: '(C2H6OSi)n',
    density: 0.98,
    solids_pct: 100,
    voc: 12,
    flash_point_c: 120,
    viscosity: '500 cP @ 25 °C',
    color_index: '',
    appearance: 'Líquido opalescente',
    hazard_class: 'No peligroso',
    packing: 'Bidón 20 kg',
    storage: 'Ambiente, cerrado',
    unit: 'kg',
    min_stock: 40,
    typical_supplier: 'BYK',
    status: 'Activa',
    has_sds: true,
    notes: 'Dosificar al final de la dispersión. Sobredosis genera cráteres.',
    updated_at: isoDaysAgo(15),
  },
  {
    id: 6,
    code: 'PG0002',
    procurement_code: 'MP-006',
    procurement_name: 'Negro de humo furnace',
    procurement_type: 'Materias primas',
    name: 'Negro de humo furnace',
    family: 'Pigmentos',
    chemical_name: 'Carbono amorfo',
    cas_number: '1333-86-4',
    chemical_formula: 'C',
    density: 1.8,
    solids_pct: 100,
    voc: 0,
    flash_point_c: null,
    viscosity: 'Sólido / polvo',
    color_index: 'PBk7',
    appearance: 'Polvo negro',
    hazard_class: 'Irritante',
    packing: 'Saco 20 kg',
    storage: 'Seco, evitar polvo en suspensión',
    unit: 'kg',
    min_stock: 30,
    typical_supplier: 'Orion',
    status: 'Activa',
    has_sds: true,
    notes: 'Usar en gris A006. Cubrebocas obligatorio al pesar.',
    updated_at: isoDaysAgo(9),
  },
  {
    id: 7,
    code: 'DY0002',
    procurement_code: 'MP-007',
    procurement_name: 'Acetato de butilo',
    procurement_type: 'Materias primas',
    name: 'Acetato de butilo',
    family: 'Diluyentes',
    chemical_name: 'Acetato de n-butilo',
    cas_number: '123-86-4',
    chemical_formula: 'C6H12O2',
    density: 0.88,
    solids_pct: 0,
    voc: 880,
    flash_point_c: 22,
    viscosity: '0,7 cP @ 20 °C',
    color_index: '',
    appearance: 'Líquido incoloro, olor frutal',
    hazard_class: 'Inflamable',
    packing: 'Tambor 180 L',
    storage: 'Bodega inflamables',
    unit: 'L',
    min_stock: 80,
    typical_supplier: 'Oxiquim',
    status: 'Obsoleta',
    has_sds: true,
    notes: 'Reemplazada gradualmente por xileno en fórmulas vigentes.',
    updated_at: isoDaysAgo(50),
  },
  {
    id: 8,
    code: 'EN0001',
    procurement_code: 'INS-001',
    procurement_name: 'Tineta metálica 16 L',
    procurement_type: 'Insumos',
    name: 'Tineta metálica 16 L',
    family: 'Envases',
    chemical_name: '',
    cas_number: '',
    chemical_formula: '',
    density: null,
    solids_pct: null,
    voc: null,
    flash_point_c: null,
    viscosity: '',
    color_index: '',
    appearance: 'Envase metálico con tapa',
    hazard_class: 'No peligroso',
    packing: 'Unidad',
    storage: 'Bodega insumos, seco',
    unit: 'UN',
    min_stock: 120,
    typical_supplier: 'Envases Sur',
    status: 'Activa',
    has_sds: false,
    notes: 'Formato comercial T-COAT 16 L.',
    updated_at: isoDaysAgo(8),
  },
  {
    id: 9,
    code: 'EQ0001',
    procurement_code: 'INS-002',
    procurement_name: 'Etiqueta tineta 16 L T-COAT',
    procurement_type: 'Insumos',
    name: 'Etiqueta tineta 16 L T-COAT',
    family: 'Etiquetas',
    chemical_name: '',
    cas_number: '',
    chemical_formula: '',
    density: null,
    solids_pct: null,
    voc: null,
    flash_point_c: null,
    viscosity: '',
    color_index: '',
    appearance: 'Rollo adhesivo impreso',
    hazard_class: 'No peligroso',
    packing: 'Rollo 500 un.',
    storage: 'Seco, lejos de solventes',
    unit: 'UN',
    min_stock: 500,
    typical_supplier: 'Gráfica Andes',
    status: 'Activa',
    has_sds: false,
    notes: 'Incluye lote, fecha y código de color.',
    updated_at: isoDaysAgo(8),
  },
];

export const seedLots = () => [
  { id: 1, material_code: 'RS0001', lot_code: 'L-RS0001-2608-01', warehouse: 'Bodega MP', quantity: 1200, unit: 'kg', received_at: isoDaysAgo(18), expiry: '2027-08-01', supplier_lot: 'DOW-4412', status: 'Disponible' },
  { id: 2, material_code: 'RS0001', lot_code: 'L-RS0001-2607-03', warehouse: 'Bodega JR 1', quantity: 640, unit: 'kg', received_at: isoDaysAgo(32), expiry: '2027-07-10', supplier_lot: 'DOW-4388', status: 'Disponible' },
  { id: 3, material_code: 'PG0001', lot_code: 'L-PG0001-2608-02', warehouse: 'Bodega MP', quantity: 400, unit: 'kg', received_at: isoDaysAgo(10), expiry: null, supplier_lot: 'KRO-9921', status: 'Disponible' },
  { id: 4, material_code: 'PG0001', lot_code: 'L-PG0001-2606-09', warehouse: 'Bodega Planta 2', quantity: 220, unit: 'kg', received_at: isoDaysAgo(48), expiry: null, supplier_lot: 'KRO-9810', status: 'Disponible' },
  { id: 5, material_code: 'DY0001', lot_code: 'L-DY0001-2608-04', warehouse: 'Bodega MP', quantity: 360, unit: 'L', received_at: isoDaysAgo(8), expiry: '2028-01-15', supplier_lot: 'OXI-778', status: 'Disponible' },
  { id: 6, material_code: 'DY0001', lot_code: 'L-DY0001-2607-11', warehouse: 'Bodega JR 1', quantity: 50, unit: 'L', received_at: isoDaysAgo(28), expiry: '2027-12-01', supplier_lot: 'OXI-741', status: 'Reservado' },
  { id: 7, material_code: 'EX0001', lot_code: 'L-EX0001-2607-02', warehouse: 'Bodega MP', quantity: 960, unit: 'kg', received_at: isoDaysAgo(22), expiry: null, supplier_lot: 'MEL-2201', status: 'Disponible' },
  { id: 8, material_code: 'AD0001', lot_code: 'L-AD0001-2608-01', warehouse: 'Bodega JR 1', quantity: 38, unit: 'kg', received_at: isoDaysAgo(5), expiry: '2027-02-01', supplier_lot: 'BYK-118', status: 'Disponible' },
  { id: 9, material_code: 'PG0002', lot_code: 'L-PG0002-2608-08', warehouse: 'Bodega MP', quantity: 24, unit: 'kg', received_at: isoDaysAgo(14), expiry: null, supplier_lot: 'ORN-55', status: 'Disponible' },
  { id: 10, material_code: 'DY0002', lot_code: 'L-DY0002-2601-02', warehouse: 'Bodega Planta 2', quantity: 0, unit: 'L', received_at: isoDaysAgo(90), expiry: '2026-09-01', supplier_lot: 'OXI-510', status: 'Agotado' },
  { id: 11, material_code: 'EN0001', lot_code: 'L-EN0001-2608-01', warehouse: 'Bodega JR 1', quantity: 180, unit: 'UN', received_at: isoDaysAgo(7), expiry: null, supplier_lot: 'ENV-331', status: 'Disponible' },
  { id: 12, material_code: 'EQ0001', lot_code: 'L-EQ0001-2608-01', warehouse: 'Bodega JR 1', quantity: 2400, unit: 'UN', received_at: isoDaysAgo(7), expiry: null, supplier_lot: 'ETQ-118', status: 'Disponible' },
];

export const seedReceptions = () => [
  { id: 1, code: 'REC-MP-041', date: isoDaysAgo(8), oc_code: 'OC-2026-118', material_code: 'DY0001', material_name: 'Xileno (mezcla de isómeros)', quantity: 360, unit: 'L', warehouse: 'Bodega MP', lot_code: 'L-DY0001-2608-04', supplier: 'Oxiquim', status: 'Recepcionada' },
  { id: 2, code: 'REC-MP-040', date: isoDaysAgo(10), oc_code: 'OC-2026-112', material_code: 'PG0001', material_name: 'Dióxido de titanio rutilo', quantity: 400, unit: 'kg', warehouse: 'Bodega MP', lot_code: 'L-PG0001-2608-02', supplier: 'Kronos', status: 'Recepcionada' },
  { id: 3, code: 'REC-MP-038', date: isoDaysAgo(18), oc_code: 'OC-2026-101', material_code: 'RS0001', material_name: 'Resina epóxica bisfenol-A', quantity: 1200, unit: 'kg', warehouse: 'Bodega MP', lot_code: 'L-RS0001-2608-01', supplier: 'Dow', status: 'Recepcionada' },
  { id: 4, code: 'REC-MP-037', date: isoDaysAgo(5), oc_code: 'OC-2026-121', material_code: 'AD0001', material_name: 'Antiespumante siliconado', quantity: 40, unit: 'kg', warehouse: 'Bodega JR 1', lot_code: 'L-AD0001-2608-01', supplier: 'BYK', status: 'Recepcionada' },
  { id: 5, code: 'REC-MP-042', date: isoDaysAgo(1), oc_code: 'OC-2026-130', material_code: 'EX0001', material_name: 'Carbonato de calcio micronizado', quantity: 250, unit: 'kg', warehouse: 'Bodega MP', lot_code: '', supplier: 'Melón Minerales', status: 'En tránsito' },
  { id: 6, code: 'REC-MP-043', date: isoDaysAgo(7), oc_code: 'OC-2026-125', material_code: 'EN0001', material_name: 'Tineta metálica 16 L', quantity: 180, unit: 'UN', warehouse: 'Bodega JR 1', lot_code: 'L-EN0001-2608-01', supplier: 'Envases Sur', status: 'Recepcionada' },
];

export const seedIngresos = () => [
  { id: 1, code: 'ING-MP-012', date: isoDaysAgo(3), material_code: 'RS0001', material_name: 'Resina epóxica bisfenol-A', quantity: 18, unit: 'kg', warehouse: 'Bodega JR 1', origin: 'Devolución de planta', reference: 'OP-2026-010', status: 'Confirmado' },
  { id: 2, code: 'ING-MP-011', date: isoDaysAgo(11), material_code: 'PG0001', material_name: 'Dióxido de titanio rutilo', quantity: 25, unit: 'kg', warehouse: 'Bodega MP', origin: 'Ajuste de inventario', reference: 'INV-ADJ-08', status: 'Confirmado' },
  { id: 3, code: 'ING-MP-010', date: isoDaysAgo(21), material_code: 'EX0001', material_name: 'Carbonato de calcio micronizado', quantity: 40, unit: 'kg', warehouse: 'Bodega MP', origin: 'Traslado interno', reference: 'TR-JR-04', status: 'Confirmado' },
];

export const seedEgresos = () => [
  { id: 1, code: 'EGR-MP-028', date: isoDaysAgo(2), material_code: 'RS0001', material_name: 'Resina epóxica bisfenol-A', quantity: 134.4, unit: 'kg', warehouse: 'Bodega JR 1', destination: 'Orden de producción', reference: 'OP-2026-014', status: 'Confirmado' },
  { id: 2, code: 'EGR-MP-027', date: isoDaysAgo(2), material_code: 'PG0001', material_name: 'Dióxido de titanio rutilo', quantity: 57.6, unit: 'kg', warehouse: 'Bodega JR 1', destination: 'Orden de producción', reference: 'OP-2026-014', status: 'Confirmado' },
  { id: 3, code: 'EGR-MP-026', date: isoDaysAgo(5), material_code: 'DY0001', material_name: 'Xileno (mezcla de isómeros)', quantity: 16, unit: 'L', warehouse: 'Bodega MP', destination: 'Orden de producción', reference: 'OP-2026-015', status: 'Confirmado' },
  { id: 4, code: 'EGR-MP-025', date: isoDaysAgo(7), material_code: 'AD0001', material_name: 'Antiespumante siliconado', quantity: 2, unit: 'kg', warehouse: 'Bodega JR 1', destination: 'Merma / muestra QC', reference: 'QC-088', status: 'Confirmado' },
  { id: 5, code: 'EGR-MP-024', date: isoDaysAgo(9), material_code: 'RS0001', material_name: 'Resina epóxica bisfenol-A', quantity: 40.3, unit: 'kg', warehouse: 'Bodega JR 1', destination: 'Orden de producción', reference: 'OP-2026-011', status: 'Confirmado' },
];

const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : fallback();
  } catch {
    return fallback();
  }
};

const writeJson = (key, value) => {
  window.localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event(RAW_MATERIALS_CHANGED_EVENT));
};

export const loadMaterials = () =>
  readJson(MATERIALS_KEY, seedMaterials).map((row) => {
    const merged = { ...emptyMaterial(), ...row };
    return {
      ...merged,
      procurement_code: String(row.procurement_code || '').trim().toUpperCase(),
      procurement_name: String(row.procurement_name || '').trim(),
      procurement_type: String(row.procurement_type || '').trim(),
      tech_params: normalizeTechParams(row),
      unit_cost:
        Number(row.unit_cost) > 0 ? Number(row.unit_cost) : DEFAULT_MATERIAL_UNIT_COSTS[row.code] || '',
    };
  });
export const loadLots = () => readJson(LOTS_KEY, seedLots);
export const loadReceptions = () => readJson(RECEPTIONS_KEY, seedReceptions);
export const loadIngresos = () => readJson(INGRESOS_KEY, seedIngresos);
export const loadEgresos = () => readJson(EGRESOS_KEY, seedEgresos);

export const saveMaterials = (rows) => writeJson(MATERIALS_KEY, rows);
export const saveLots = (rows) => writeJson(LOTS_KEY, rows);
export const saveReceptions = (rows) => writeJson(RECEPTIONS_KEY, rows);
export const saveIngresos = (rows) => writeJson(INGRESOS_KEY, rows);
export const saveEgresos = (rows) => writeJson(EGRESOS_KEY, rows);

export const stockStatus = (qty, minStock) => {
  const quantity = Number(qty || 0);
  const min = Number(minStock || 0);
  if (quantity <= 0) return 'Sin stock';
  if (min > 0 && quantity <= min) return 'Bajo stock';
  return 'Activo';
};

export const lotsForMaterial = (lots, code) =>
  (lots || []).filter((lot) => lot.material_code === code);

export const stockFromLots = (lots, code) =>
  lotsForMaterial(lots, code).reduce((acc, lot) => acc + Number(lot.quantity || 0), 0);

export const consolidateMaterials = (materials, lots) =>
  (materials || []).map((material) => {
    const materialLots = lotsForMaterial(lots, material.code);
    const qty = materialLots.reduce((acc, lot) => acc + Number(lot.quantity || 0), 0);
    const warehouses = [...new Set(materialLots.filter((lot) => Number(lot.quantity) > 0).map((lot) => lot.warehouse))];
    return {
      ...material,
      stock_qty: qty,
      warehouses: warehouses.join(' · ') || '—',
      warehouse_count: warehouses.length,
      lots_count: materialLots.length,
      stock_status: stockStatus(qty, material.min_stock),
    };
  });

export const DEFAULT_MATERIAL_UNIT_COSTS = {
  RS0001: 4500,
  PG0001: 3800,
  DY0001: 1200,
  EX0001: 180,
  AD0001: 12500,
  PG0002: 8200,
  DY0002: 1400,
  EN0001: 4200,
  EQ0001: 80,
};

export const densityOf = (material) => {
  const fromParam = (material?.tech_params || []).find((row) => /densidad/i.test(String(row.name || '')));
  const raw = fromParam?.ideal ?? material?.density;
  const n = Number(String(raw ?? '').replace(',', '.'));
  if (Number.isFinite(n) && n > 0) return n;
  return String(material?.unit || '').toLowerCase() === 'l' ? 0.87 : 1;
};

export const quantityToKg = (qty, unit, material) => {
  const n = Number(qty || 0);
  const converted = inventoryQtyFromPurchase(n, unit);
  if (converted.unit === 'Kg') return converted.qty;
  if (converted.unit === 'L') return converted.qty * densityOf(material);
  const u = String(unit || material?.unit || '').trim().toLowerCase();
  if (u === 'kg') return n;
  if (u === 'l') return n * densityOf(material);
  return 0;
};

export const unitCostOf = (material, catalogByCode = {}) => {
  const own = Number(material?.unit_cost);
  if (Number.isFinite(own) && own > 0) return own;
  const sku = String(material?.procurement_code || '').trim().toUpperCase();
  const fromCatalog = Number(
    catalogByCode[sku]?.last_unit_price ?? catalogByCode[sku]?.unit_price ?? catalogByCode[sku]?.unit_cost
  );
  if (Number.isFinite(fromCatalog) && fromCatalog > 0) return fromCatalog;
  return Number(DEFAULT_MATERIAL_UNIT_COSTS[material?.code] || 0);
};

export const inventoryValueOf = (qty, material, catalogByCode = {}) =>
  Number(qty || 0) * unitCostOf(material, catalogByCode);

export const formatMoneyClp = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const monthEnd = (year, monthIndex) => new Date(year, monthIndex + 1, 0, 23, 59, 59);

export const lastCalendarMonths = (count = 8) => {
  const out = [];
  const now = new Date();
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    out.push({
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('es-CL', { month: 'short' }).replace('.', ''),
      year: date.getFullYear(),
      month: date.getMonth(),
      start: new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0),
      end: monthEnd(date.getFullYear(), date.getMonth()),
    });
  }
  return out;
};

const inMonth = (value, period) => {
  const date = new Date(`${String(value || '').slice(0, 10)}T12:00:00`);
  return !Number.isNaN(date.getTime()) && date >= period.start && date <= period.end;
};

export const inventoryTimeSeries = ({
  materials = [],
  lots = [],
  ingresos = [],
  egresos = [],
  catalogByCode = {},
  selectedCodes = null,
}) => {
  const codes = Array.isArray(selectedCodes)
    ? selectedCodes
    : (materials || []).map((row) => row.code);
  const allowed = new Set(codes);
  const byCode = Object.fromEntries((materials || []).map((row) => [row.code, row]));
  const stock = {};
  codes.forEach((code) => {
    stock[code] = lotsForMaterial(lots, code).reduce((acc, lot) => acc + Number(lot.quantity || 0), 0);
  });
  const movements = [
    ...(ingresos || [])
      .filter((row) => String(row.status || '') !== 'Anulado' && allowed.has(row.material_code))
      .map((row) => ({ date: row.date, code: row.material_code, delta: Number(row.quantity || 0) })),
    ...(egresos || [])
      .filter((row) => String(row.status || '') !== 'Anulado' && allowed.has(row.material_code))
      .map((row) => ({ date: row.date, code: row.material_code, delta: -Number(row.quantity || 0) })),
  ];
  const months = lastCalendarMonths(8);
  const snapshots = new Array(months.length);
  for (let index = months.length - 1; index >= 0; index -= 1) {
    snapshots[index] = { ...stock };
    movements.forEach((move) => {
      if (!inMonth(move.date, months[index])) return;
      stock[move.code] = Number(stock[move.code] || 0) - move.delta;
    });
  }
  return months.map((period, index) => {
    let kg = 0;
    let amount = 0;
    codes.forEach((code) => {
      const material = byCode[code];
      if (!material) return;
      const qty = Math.max(0, Number(snapshots[index]?.[code] || 0));
      kg += quantityToKg(qty, material.unit, material);
      amount += inventoryValueOf(qty, material, catalogByCode);
    });
    return { key: period.key, label: period.label, kg, amount };
  });
};

export const emptyMaterial = () => ({
  id: null,
  code: '',
  name: '',
  family: 'Resinas',
  chemical_name: '',
  cas_number: '',
  chemical_formula: '',
  density: '',
  solids_pct: '',
  voc: '',
  flash_point_c: '',
  viscosity: '',
  color_index: '',
  appearance: '',
  hazard_class: 'No peligroso',
  packing: '',
  storage: '',
  unit: 'kg',
  unit_cost: '',
  min_stock: 0,
  typical_supplier: '',
  status: 'Activa',
  has_sds: true,
  notes: '',
  procurement_item_id: null,
  procurement_code: '',
  procurement_name: '',
  procurement_type: '',
  tech_params: [],
});

export const emptyTechParam = () => ({
  key: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  unit: '',
  ideal: '',
  min: '',
  max: '',
});

export const emptyReception = () => ({
  reception_id: null,
  purchase_receipt_id: null,
  purchase_order_id: null,
  material_id: null,
  material_code: '',
  date: new Date().toISOString().slice(0, 10),
  quantity: '',
  unit: 'kg',
  warehouse: 'Bodega MP',
  lot_code: '',
  supplier: '',
  supplier_lot: '',
  expiry: '',
  oc_code: '',
  batch_params: [],
});

/** Recepción pendiente de ingresar a inventario de planta (pestaña Recepción). */
export const isPendingReception = (row) => {
  const status = String(row?.status || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return status === 'pendiente' || status === 'por ingresar' || status === 'recepcionada';
};

export const batchParamsFromMaterial = (material) =>
  (material?.tech_params || []).map((item) => ({
    key: item.key || emptyTechParam().key,
    name: item.name || '',
    unit: item.unit || '',
    ideal: item.ideal ?? '',
    min: item.min ?? '',
    max: item.max ?? '',
    measured: '',
  }));

export const nextLotCode = (lots, materialCode, dateStr) => {
  const code = String(materialCode || '').trim().toUpperCase();
  if (!code) return '';
  const date = dateStr ? new Date(`${dateStr}T00:00:00`) : new Date();
  const yymm = `${String(date.getFullYear()).slice(2)}${String(date.getMonth() + 1).padStart(2, '0')}`;
  const prefix = `L-${code}-${yymm}-`;
  const max = (lots || [])
    .map((row) => String(row.lot_code || '').toUpperCase())
    .filter((value) => value.startsWith(prefix.toUpperCase()))
    .map((value) => Number(value.slice(prefix.length)))
    .filter((n) => Number.isFinite(n))
    .reduce((acc, n) => Math.max(acc, n), 0);
  return `${prefix}${String(max + 1).padStart(2, '0')}`;
};

export const measuredSpecStatus = (param) => {
  const raw = String(param?.measured ?? '').trim();
  if (!raw) return '';
  const n = Number(raw.replace(',', '.'));
  if (!Number.isFinite(n)) return '';
  const min = Number(String(param.min ?? '').replace(',', '.'));
  const max = Number(String(param.max ?? '').replace(',', '.'));
  if (param.min !== '' && param.min != null && Number.isFinite(min) && n < min) return 'low';
  if (param.max !== '' && param.max != null && Number.isFinite(max) && n > max) return 'high';
  return 'ok';
};

export const TECH_PARAM_SUGGESTIONS_BASE = [
  { name: 'Densidad', unit: 'g/cm³' },
  { name: 'Sólidos', unit: '%' },
  { name: 'VOC', unit: 'g/L' },
  { name: 'Punto de inflamación', unit: '°C' },
  { name: 'Viscosidad', unit: 'cP' },
  { name: 'Fórmula química', unit: '' },
  { name: 'Índice de color', unit: '' },
];

/** Static defaults; prefer getTechParamSuggestions() for UI lists. */
export const TECH_PARAM_SUGGESTIONS = TECH_PARAM_SUGGESTIONS_BASE;

export const getTechParamSuggestions = () => {
  const map = new Map();
  [...TECH_PARAM_SUGGESTIONS_BASE, ...loadCustomTechParamSuggestions()].forEach((row) => {
    const name = String(row?.name || '').trim();
    if (!name) return;
    if (!map.has(name)) map.set(name, { name, unit: String(row?.unit || '').trim() });
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'es'));
};

export const upsertTechParamSuggestion = ({ name, unit = '' }) => {
  const cleanName = String(name || '').trim();
  const cleanUnit = String(unit || '').trim();
  if (!cleanName) return { ok: false, error: 'Indica el nombre del parámetro.' };
  const defaults = TECH_PARAM_SUGGESTIONS_BASE.some(
    (row) => row.name.toLowerCase() === cleanName.toLowerCase()
  );
  if (defaults) {
    return { ok: true, name: cleanName, unit: cleanUnit, existing: true };
  }
  const custom = loadCustomTechParamSuggestions().filter(
    (row) => row.name.toLowerCase() !== cleanName.toLowerCase()
  );
  custom.push({ name: cleanName, unit: cleanUnit });
  saveCustomTechParamSuggestions(custom);
  return { ok: true, name: cleanName, unit: cleanUnit, existing: false };
};

const LEGACY_TECH_FIELDS = [
  ['density', 'Densidad', 'g/cm³'],
  ['solids_pct', 'Sólidos', '%'],
  ['voc', 'VOC', 'g/L'],
  ['flash_point_c', 'Punto de inflamación', '°C'],
  ['viscosity', 'Viscosidad', 'cP'],
  ['chemical_formula', 'Fórmula química', ''],
  ['color_index', 'Índice de color', ''],
];

const numericRange = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw || /[a-záéíóúñ]/i.test(raw)) return { ideal: raw, min: '', max: '' };
  const n = Number(raw.replace(',', '.'));
  if (!Number.isFinite(n)) return { ideal: raw, min: '', max: '' };
  const span = Math.abs(n) * 0.05 || 0.01;
  const decimals = Number.isInteger(n) ? 0 : 3;
  return {
    ideal: raw,
    min: String(Number((n - span).toFixed(decimals))),
    max: String(Number((n + span).toFixed(decimals))),
  };
};

export const normalizeTechParams = (row) => {
  if (Array.isArray(row?.tech_params) && row.tech_params.length) {
    return row.tech_params.map((item) => ({
      key: item.key || emptyTechParam().key,
      name: String(item.name || '').trim(),
      unit: String(item.unit || '').trim(),
      ideal: item.ideal ?? '',
      min: item.min ?? '',
      max: item.max ?? '',
    }));
  }
  return LEGACY_TECH_FIELDS.filter(([field]) => {
    const value = row?.[field];
    return value !== '' && value != null;
  }).map(([field, name, unit]) => {
    const ranged = numericRange(row[field]);
    return {
      ...emptyTechParam(),
      name,
      unit: field === 'viscosity' && /cP|s/i.test(String(row[field])) ? '' : unit,
      ideal: ranged.ideal,
      min: ranged.min,
      max: ranged.max,
    };
  });
};

export const sanitizeTechParams = (params) =>
  (params || [])
    .map((item) => ({
      key: item.key || emptyTechParam().key,
      name: String(item.name || '').trim(),
      unit: String(item.unit || '').trim(),
      ideal: item.ideal === '' || item.ideal == null ? '' : item.ideal,
      min: item.min === '' || item.min == null ? '' : item.min,
      max: item.max === '' || item.max == null ? '' : item.max,
    }))
    .filter((item) => item.name);

export const techParamRangeError = (params) => {
  const invalid = (params || []).find((item) => {
    if (!item.name) return false;
    const min = Number(String(item.min ?? '').replace(',', '.'));
    const max = Number(String(item.max ?? '').replace(',', '.'));
    return item.min !== '' && item.max !== '' && Number.isFinite(min) && Number.isFinite(max) && min > max;
  });
  return invalid ? `En “${invalid.name}” el mínimo no puede ser mayor que el máximo.` : '';
};

export const nextMaterialCode = (materials, family) => {
  const map = getMaterialCategoryPrefixMap();
  const prefix = map[family] || map.Resinas || 'RS';
  const pattern = new RegExp(`^${prefix}(\\d{4})$`, 'i');
  const max = (materials || [])
    .map((row) => {
      const match = String(row.code || '').trim().toUpperCase().match(pattern);
      return match ? Number(match[1]) : 0;
    })
    .reduce((acc, n) => Math.max(acc, n), 0);
  return `${prefix}${String(max + 1).padStart(4, '0')}`;
};

export const isMaterialCode = (code) => /^[A-Z]{2}\d{4}$/i.test(String(code || '').trim());

export const activeMaterials = (materials) =>
  (materials || []).filter((row) => row.status === 'Activa');

export const procurementOptionsFromCatalog = (
  catalog = [],
  materials = [],
  currentCode = '',
  _family = 'Resinas'
) => {
  const current = String(currentCode || '').trim().toUpperCase();
  const used = new Set(
    (materials || [])
      .map((row) => String(row.procurement_code || '').trim().toUpperCase())
      .filter((code) => code && code !== current)
  );
  const byCode = new Map();
  const add = (code, name, type, id = null) => {
    const value = String(code || '').trim().toUpperCase();
    if (!value || used.has(value)) return;
    if (!byCode.has(value)) {
      const labelName = String(name || '').trim();
      const typeLabel = String(type || '').trim();
      byCode.set(value, {
        value,
        id: id || null,
        label: [value, labelName, typeLabel && typeLabel !== labelName ? typeLabel : '']
          .filter(Boolean)
          .join(' · '),
        name: labelName,
        item_type: typeLabel,
      });
    }
  };
  (catalog || []).forEach((item) => {
    if (!isLinkableProcurementItem(item)) return;
    add(item.item_code || item.sku || item.code, item.description || item.name, item.item_type || item.kind, item.id);
  });
  if (!byCode.size) {
    (materials || []).forEach((row) => {
      if (!isLinkableProcurementItem({
        item_code: row.procurement_code,
        item_type: row.procurement_type,
      }) && !isLinkableProcurementItem({ item_code: row.procurement_code })) {
        return;
      }
      add(row.procurement_code, row.procurement_name || row.name, row.procurement_type, row.procurement_item_id);
    });
  }
  if (current && !byCode.has(current)) {
    const linked = (materials || []).find(
      (row) => String(row.procurement_code || '').trim().toUpperCase() === current
    );
    add(current, linked?.procurement_name || linked?.name || '', linked?.procurement_type || '', linked?.procurement_item_id);
  }
  return [...byCode.values()].sort((a, b) => a.value.localeCompare(b.value, 'es'));
};
